import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const UPDATED = "June 2026";

export default function Privacy() {
  return (
    <div className="pt-28 pb-24 max-w-3xl mx-auto px-5" data-testid="privacy-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37] flex items-center gap-2"><ShieldCheck size={14} /> Legal</p>
      <h1 className="font-display text-4xl md:text-5xl text-white mt-3 mb-2">Privacy Policy</h1>
      <p className="text-white/40 text-sm mb-10">Last updated: {UPDATED}</p>

      <div className="space-y-8 text-white/70 text-sm leading-relaxed">
        <section>
          <p>XMD Productions ("XMD Productions," "we," "us," or "our") respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have when you use our website and booking services.</p>
        </section>

        <Section title="Information We Collect">
          <p>When you book a session or contact us, we may collect: your first and last name, email address, phone number, optional date of birth, project details you provide, and booking and payment information. Payment card details are processed by our payment providers (Stripe and PayPal) and are not stored on our servers.</p>
        </Section>

        <Section title="How We Use Your Information">
          <ul className="list-disc pl-5 space-y-1">
            <li>To create, confirm, and manage your bookings.</li>
            <li>To send booking confirmations, receipts, reminders, and service updates.</li>
            <li>To respond to your inquiries and provide customer support.</li>
            <li>To process payments and prevent fraud.</li>
            <li>To improve our services and comply with legal obligations.</li>
          </ul>
        </Section>

        <Section title="SMS / Text Messaging">
          <p>If you opt in to SMS by checking the consent box on our booking form, we may send you transactional text messages such as appointment confirmations, reminders, and updates. SMS consent is optional and is never a condition of purchase or booking.</p>
          <p className="mt-3 text-white font-medium">No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Information sharing to subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>
          <p className="mt-3">Message frequency varies. Message and data rates may apply. You can opt out at any time by replying <span className="text-[#D4AF37]">STOP</span>, or reply <span className="text-[#D4AF37]">HELP</span> for assistance. Carriers are not liable for delayed or undelivered messages.</p>
        </Section>

        <Section title="How We Share Information">
          <p>We share information only with service providers who help us operate our business — such as payment processors (Stripe, PayPal), email delivery, and SMS delivery (Twilio) — strictly to provide the services you request. We do not sell your personal information. We may disclose information if required by law.</p>
        </Section>

        <Section title="Data Retention & Security">
          <p>We retain booking and customer records as long as necessary to provide our services and meet legal, accounting, or reporting requirements. We use reasonable administrative and technical safeguards to protect your information.</p>
        </Section>

        <Section title="Your Choices">
          <p>You may request access to, correction of, or deletion of your personal information by contacting us. You can unsubscribe from marketing emails using the link in those emails, and from SMS by replying STOP.</p>
        </Section>

        <Section title="Contact Us">
          <p>XMD Productions<br />3020 High Ridge Rd, Suite 600A, Boynton Beach, FL 33426<br />Phone: +1 561-503-7743<br />Email: <a href="mailto:info@xmdproductions.com" className="text-[#D4AF37] hover:underline">info@xmdproductions.com</a></p>
        </Section>

        <p className="pt-4 border-t border-white/10">See also our <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms &amp; Conditions</Link>.</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl text-white mb-2">{title}</h2>
      {children}
    </section>
  );
}
